import { useReadQuery } from "@apollo/client";
import { GridProps, Typography } from "@mui/material";
import { grey, indigo } from "@mui/material/colors";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import DonutChart from "components/Dashboard/DonutChart";
import { useCallback, useMemo } from "react";

import { useSubmissionDonutLegend } from "./donutLegend";
import { useWasteSubmissionsOverviewContext } from "./WasteSubmissionsOverviewContext";

// Generator status colors matching waste overview palette
const GENERATOR_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  LARGE_QUANTITY_GENERATOR: {
    label: "LQG",
    color: indigo[600],
  },
  SMALL_QUANTITY_GENERATOR: {
    label: "SQG",
    color: indigo[300],
  },
  VERY_SMALL_QUANTITY_GENERATOR: {
    label: "VSQG",
    color: indigo[200],
  },
  NONE: {
    label: "None",
    color: grey[300],
  },
};

type SubmissionGeneratorStatusTileProps = GridProps & {
  onGeneratorStatusClick?: (status: string) => void;
  selectedGeneratorStatus?: string | null;
};

export function SubmissionGeneratorStatusTile({
  onGeneratorStatusClick,
  selectedGeneratorStatus,
  ...gridProps
}: SubmissionGeneratorStatusTileProps) {
  return (
    <DashboardTile {...gridProps}>
      <SubmissionGeneratorStatusContent
        onGeneratorStatusClick={onGeneratorStatusClick}
        selectedGeneratorStatus={selectedGeneratorStatus}
      />
    </DashboardTile>
  );
}

// Export content component for use in unified container
export { SubmissionGeneratorStatusContent };

function SubmissionGeneratorStatusContent({
  onGeneratorStatusClick,
  selectedGeneratorStatus,
}: Pick<SubmissionGeneratorStatusTileProps, "onGeneratorStatusClick" | "selectedGeneratorStatus">) {
  const { byGeneratorStatusQuery } = useWasteSubmissionsOverviewContext();
  const { legend, legendSx } = useSubmissionDonutLegend();
  const { data } = useReadQuery(byGeneratorStatusQuery);

  const summary = data?.wasteSubmissionsSummary;
  const total = summary?.total ?? 0;

  const chartData = useMemo(() => {
    if (!summary?.byGeneratorStatus || summary.byGeneratorStatus.length === 0) return [];

    return summary.byGeneratorStatus.map((item) => {
      const config = GENERATOR_STATUS_CONFIG[item.generatorStatus] ?? {
        label: item.generatorStatus,
        color: grey[400],
      };
      return {
        id: item.generatorStatus,
        value: item.count,
        label: `${config.label} (${item.count})`,
        color: config.color,
      };
    });
  }, [summary]);

  const selectedIndex = useMemo(() => {
    if (!selectedGeneratorStatus) return null;
    return chartData.findIndex((item) => item.id === selectedGeneratorStatus);
  }, [selectedGeneratorStatus, chartData]);

  const handleItemClick = useCallback(
    (
      _event: React.MouseEvent<SVGPathElement, MouseEvent>,
      itemIdentifier: { dataIndex: number }
    ) => {
      const clickedItem = chartData[itemIdentifier.dataIndex];
      if (clickedItem && onGeneratorStatusClick) {
        onGeneratorStatusClick(clickedItem.id as string);
      }
    },
    [chartData, onGeneratorStatusClick]
  );

  const handleLegendItemClick = useCallback(
    (item: { id?: string | number }) => {
      if (item.id && onGeneratorStatusClick) {
        onGeneratorStatusClick(item.id as string);
      }
    },
    [onGeneratorStatusClick]
  );

  return (
    <>
      <DashboardTile.Header title="By Generator Status" infoTooltip={<GeneratorStatusTooltip />} />
      <DashboardTile.ContentArea>
        {chartData.length > 0 ? (
          <DonutChart
            seriesData={chartData}
            containerHeight="250px"
            label={total.toString()}
            subLabel="Total"
            selectedIndex={selectedIndex}
            onItemClick={handleItemClick}
            onLegendItemClick={handleLegendItemClick}
            legend={legend}
            legendSx={legendSx}
            colors={chartData.map((item) => item.color)}
          />
        ) : (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No submissions found
          </Typography>
        )}
      </DashboardTile.ContentArea>
    </>
  );
}

function GeneratorStatusTooltip() {
  return (
    <Typography variant="caption">
      <Typography component="span" variant="caption" fontWeight={800}>
        By Generator Status
      </Typography>{" "}
      shows submission distribution by facility generator status (LQG, SQG, VSQG). Click a segment
      to filter.
    </Typography>
  );
}
